import { createContext } from "react";
import type { BizKey, MockMode, SpaceKey, SpaceMembership, TenantKey } from "@/domain/space";
import type { Permission } from "@/domain/user";

/**
 * 공간(space) Context — 객체·타입만 둔다(Fast Refresh: Provider 는 SpaceContext.tsx).
 * 소속 = 실제 `tenants/{t}/members/{uid}` 문서(2026-10-23 D 묶음). 개발 서버 `?mock=` 일 때만 가상 소속.
 */
export interface SwitchSpaceOptions {
  /** 지정하면 D-03 판정 없이 이 경로로 이동 (전체 보기·검색에서 특정 메뉴로 갈 때) */
  to?: string;
}

export interface SpaceContextValue {
  /** 지금 공간 — URL 이 정본(/my… = 내 업무, /c/{tenant}/{biz}/…·/c/{tenant}/… = 법인). 공간 밖 경로(옛 주소·없는 주소)면 마지막 공간 */
  space: SpaceKey;
  /** 지금 법인 (내 업무면 null) */
  tenant: TenantKey | null;
  /** 지금 사업 — 사업 메뉴 주소(/c/{t}/{biz}/…)일 때만. 법인 공통 메뉴·내 업무면 null (D-26) */
  biz: BizKey | null;
  /**
   * 데이터 조회 기준 법인 — 법인 공간이면 그 법인, 내 업무면 옛 기본(중앙).
   * 월별 캐시 키·중앙 전용 구독 판정에 쓴다. 2단계에서 tenants/{법인}/… 경로 선택에도 쓴다.
   */
  dataTenant: TenantKey;
  /** 이 사용자의 기본 공간 — 소속 아닌 법인 주소·루트("/") 진입 시 여기로 */
  defaultSpace: SpaceKey;
  /**
   * 법인 단위 권한 판정. tenant 생략 = 지금 법인(내 업무면 법인 무관 판정).
   * 소속 아닌 법인이면 false, 소속이면 useAuth().hasPerm(key, tenant).
   */
  hasPermIn: (key: Permission, tenant?: TenantKey) => boolean;
  /** 메뉴 key → 법인 주소. tenant 생략 = dataTenant. 지금 법인이면 지금 사업을 유지 */
  tenantPath: (key: string, tenant?: TenantKey) => string;
  /** 열 수 있는 공간 (항상 "my" 포함, 순서 = 표시 순서) */
  spaces: SpaceKey[];
  /** 근무 법인 (업무 공간) */
  tenants: TenantKey[];
  /**
   * 내 소속 사업부(다중, 2026-10-26) — 법인별 활성 소속 문서의 bizs. 소속 문서가 없거나(전체 admin·옛 계정) 비었으면 키 없음.
   * 화면 표시·알림 묶음용(권한 판정에는 쓰지 않음 — rules tp 도 사업부를 보지 않음).
   */
  myBizs: Partial<Record<TenantKey, BizKey[]>>;
  membership: SpaceMembership;
  /** 소속 확인 끝(전체 admin 은 처음부터 true). false 동안 SpaceProvider 는 화면을 그리지 않는다 */
  membershipLoaded: boolean;
  /** 법인 관리자 판정 — 전체 admin 이거나 그 법인 소속 문서 tenant_admin(활성). 법인 설정 진입·사용자 관리 변형에 쓴다 */
  canManageTenant: (tenant: TenantKey) => boolean;
  /** 미리보기 가상 소속(개발 서버 `?mock=`). 실제 소속이면 null */
  mockMode: MockMode | null;
  /** 같은 법인 안에서 사업 전환 — 보던 사업 메뉴가 있으면 같은 메뉴, 아니면 그 사업 홈 */
  switchBiz: (tenant: TenantKey, biz: BizKey) => void;
  /** 공간 전환. D-03 — 보던 메뉴가 새 법인에도 있고 권한도 있으면 같은 메뉴(주소의 법인만 교체), 아니면 그 공간 홈 */
  switchSpace: (next: SpaceKey, opts?: SwitchSpaceOptions) => void;
  /** 미리보기 실험: 법인 공간에서 사이드바를 법인 색조로 (PreviewNote 토글, localStorage 기억) */
  sidebarTint: boolean;
  setSidebarTint: (on: boolean) => void;
}

export const SpaceContext = createContext<SpaceContextValue | null>(null);
